import * as Sentry from '@sentry/nextjs';
import axios from 'data/axios';
import React, { useContext, useEffect, useState } from 'react';

import AppContext from '../../contexts/AppContext';
import Button from '../buttons/Button';
import PhotoUploader from '../fileUploads/PhotoUploader';
import LoadingProgressCircle from '../LoadingProgressCircle';
import BaseModal from './BaseModal';
import { Mixpanel } from 'utils/Mixpanel';
import useGTM from 'hooks/useGTM';
import { AnalyticsEvent } from 'models/Analytics';
import { Upload } from 'react-feather';
import useUser from 'hooks/useUser';

function PoiPhotoUploadModal({ open, handleClose, poi }) {
    const { user } = useUser();
    const [files, setFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState(0);
    const [thumbs, setThumbs] = useState([]);
    const [step, setStep] = useState<number | string>(1);
    const [uploadProgress, setUploadProgress] = useState(0);
    const { sendEvent } = useGTM();

    const { state } = useContext(AppContext);
    const { photoContest } = state;
    const photoContestActive = photoContest.state === 'ACTIVE';

    useEffect(() => {
        if (open) {
            setStep(1);
            Mixpanel.track('Photo upload modal opened');
        }
    }, [open]);

    const filesChanged = (files, thumbs) => {
        setFiles(files);
        setThumbs(thumbs);
    };

    const handleSubmit = async () => {
        setStep(2);
        Mixpanel.track('Photo upload submit');

        const formData = new FormData();
        files.forEach((f, i) => formData.set(`file${i}`, f));

        let errorWhileUploading = false;

        for (let i = 0; i < files.length; i++) {
            const uploadObject = {
                file: files[i],
                fileIndex: i,
                onUploadProgress: handleUploadProgress
            };
            try {
                await uploadFile(uploadObject);
                setUploadedFiles((files) => files + 1);
            } catch (error) {
                Sentry.captureException(error);
                errorWhileUploading = true;
            }
        }

        if (errorWhileUploading) {
            sendEvent({ action: AnalyticsEvent.PHOTO_UPLOAD_ERROR, params: { userEmail: user.email } });

            Mixpanel.track('Photo upload error', {
                userEmail: user.email
            });

            return onError();
        }

        Mixpanel.track('Photo upload success');

        sendEvent({ action: AnalyticsEvent.PHOTOS_UPLOADED, params: { userEmail: user.email } });
        reset();
        setStep(3);
    };

    const onError = () => setStep('error');

    const uploadFile = async ({ file, onUploadProgress, fileIndex }) => {
        const form = new FormData();
        form.append('file', file);
        form.append('poi_id', poi.id);

        try {
            await axios.post(`/poi/${poi.id}/media`, form, {
                onUploadProgress: (progressEvent) =>
                    onUploadProgress(fileIndex, (progressEvent.loaded / progressEvent.total) * 100)
            });
        } catch (error) {
            throw new Error(error);
        }
    };

    const handleUploadProgress = (fileIndex, percentage) => {
        const progressPerFile = 100 / files.length;
        const totalProgress = fileIndex * progressPerFile + percentage / files.length;
        setUploadProgress(totalProgress);
    };

    const reset = () => {
        setFiles([]);
        setThumbs([]);
        setUploadProgress(0);
    };

    const onClose = () => {
        reset();
        handleClose();
        setUploadProgress(0);
    };

    return (
        <BaseModal open={open} handleClose={onClose}>
            <div className="w-full h-full sm:w-[650px] sm:relative sm:h-auto py-4 bg-white text-center flex flex-col justify-between">
                {step < 4 && (
                    <div className="header px-6">
                        <div className="flex gap-4 text-left">
                            {photoContestActive && poi.is_in_contest && (
                                <img src="/images/foto-izbor/badge.svg" width="63" height="100" alt="" />
                            )}
                            <div>
                                <h3 className="mb-2 ">
                                    Naloži fotografije — <span className="text-brand">{poi.name}</span>
                                </h3>
                                {step === 1 && (
                                    <p>
                                        {photoContestActive && poi.is_in_contest && (
                                            <>
                                                {' '}
                                                Z naloženimi fotografijami sodeluješ v nagradnem izboru za glavno
                                                nagrado <span className="text-brand font-bold">Nikon Z 30</span> in{' '}
                                                <span className="text-brand font-bold">
                                                    za eno od 15 majic Kam za vikend.
                                                </span>
                                            </>
                                        )}
                                    </p>
                                )}
                                {step === 2 && <p>Fotografije se nalagajo ...</p>}
                            </div>
                        </div>
                    </div>
                )}
                <div className="content px-6 py-4 overflow-auto">
                    {step === 1 && (
                        <PhotoUploader
                            handleFilesChange={filesChanged}
                            maxPhotos={10}
                            initialFiles={files}
                            initialThumbs={thumbs}
                            enableDescriptions
                        />
                    )}
                    {step === 2 && <LoadingProgressCircle percentage={uploadProgress} />}
                    {step === 3 && (
                        <div>
                            <h2 className="mb-6">Fotografije uspešno naložene 🤘</h2>
                            <h3>Objavljene bodo, ko jih pregledajo in odobrijo naši administratorji.</h3>
                            {photoContestActive && poi.is_in_contest && (
                                <>
                                    <p className="mt-2 max-w-sm mx-auto">
                                        Zdaj sodeluješ v nagradnem izboru za glavno nagrado{' '}
                                        <span className="text-brand font-bold">Nikon Z 30</span> in{' '}
                                        <span className="text-brand font-bold">15 majic Kam za vikend.</span>
                                    </p>
                                    <p className="mt-2  max-w-sm mx-auto">
                                        <strong>Ne pozabi</strong>, več fotografij kot naložiš, več možnosti imaš, da
                                        boš ravno ti prejel glavno nagrado.
                                    </p>
                                </>
                            )}
                            <p className="mt-8">Vikend poln dogodivščin ti želi ekipa Kam za vikend.</p>
                        </div>
                    )}
                    {step === 'error' && (
                        <div>
                            <h2>Nekaj fotografij morda ni bilo naloženih 🧐</h2>
                            <p className="mt-4">
                                Naloženih je bilo{' '}
                                <strong>
                                    {uploadedFiles} od
                                    {files.length} fotografij
                                </strong>
                                .
                            </p>
                            <p className="mt-2">
                                Preveri, da fotografije niso prevelike in da ustrezajo formatu .png ali .jpg. Preveri
                                tudi, da imaš stabilno internetno povezavo (WiFi) potem pa osveži stran in poskusi
                                znova.
                            </p>
                        </div>
                    )}
                </div>
                <div className="footer">
                    <div className="px-6 flex items-center justify-between border-tborder-gray-200 pt-4 border-t border-gray-100">
                        {step === 1 && (
                            <>
                                <Button variant="text" color="secondary" onClick={onClose}>
                                    Zapri
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={files.length === 0}
                                    onClick={() => handleSubmit()}
                                    endIcon={<Upload />}
                                >
                                    Naloži
                                </Button>
                            </>
                        )}
                        {step === 2 && (
                            <Button variant="text" color="secondary" onClick={() => onClose()}>
                                Prekliči
                            </Button>
                        )}
                        {step === 3 && (
                            <>
                                <Button variant="text" color="secondary" onClick={() => setStep(1)}>
                                    Naloži dodatne fotografije
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        onClose();
                                    }}
                                >
                                    Končaj
                                </Button>
                            </>
                        )}
                        {step === 'error' && (
                            <>
                                <Button variant="text" color="secondary" onClick={() => onClose()}>
                                    Zapri
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        window.location.reload();
                                    }}
                                >
                                    Osveži stran in poskusi znova
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </BaseModal>
    );
}

export default PoiPhotoUploadModal;
